import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { auth } from '../../../firebase';

const PaymentHistory = () => {
  const { t } = useTranslation();

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(3);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);

  // Fetch payment history
  const fetchPaymentHistory = async (page = 1, size = 10) => {
    setLoading(true);
    setError(null);

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await axios.get('https://api.servicegest.ro/driver/payment/history', {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        params: {
          page,
          size,
          orderAsc: false // Newest first
        }
      });

      console.log('Payment history API response:', response.data);

      if (response.data && Array.isArray(response.data)) {
        const payments = response.data;
        setPaymentHistory(payments);

        // Simple pagination logic based on response size
        const currentHasMorePages = payments.length === size;
        const calculatedTotalPages = currentHasMorePages ? page + 1 : page;

        setTotalPages(calculatedTotalPages);
        setTotalElements(payments.length);
        setHasMorePages(currentHasMorePages);

        console.log('Pagination state:', {
          currentPage: page,
          totalPages: calculatedTotalPages,
          totalElements: payments.length,
          receivedItems: payments.length,
          pageSize: size,
          hasMorePages: currentHasMorePages
        });
      } else {
        setPaymentHistory([]);
        setTotalPages(0);
        setTotalElements(0);
        setHasMorePages(false);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setError(t('paymentHistory.errorLoading'));
      setPaymentHistory([]);
      setTotalPages(0);
      setTotalElements(0);
      setHasMorePages(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when page changes
  useEffect(() => {
    fetchPaymentHistory(currentPage, pageSize);
  }, [currentPage, pageSize]);

  // Format date
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'PAID':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            {t('paymentHistory.status.paid')}
          </span>
        );
      case 'UNSUCCESSFUL':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
            <XCircleIcon className="w-3 h-3 mr-1" />
            {t('paymentHistory.status.unsuccessful')}
          </span>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            {t('paymentHistory.status.pending')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400">
            {status}
          </span>
        );
    }
  };

  // Get payment type badge
  const getPaymentTypeBadge = (type) => {
    switch (type) {
      case 'BALANCE':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400">
            {t('paymentHistory.type.balance')}
          </span>
        );
      case 'CARD':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
            {t('paymentHistory.type.card')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400">
            {type}
          </span>
        );
    }
  };

  // Handle page navigation
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMorePages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
        <div className="p-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <CreditCardIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 dark:text-white">
                {t('paymentHistory.title')}
              </h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('paymentHistory.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-purple-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-slate-600 dark:text-slate-400">
                {t('paymentHistory.loading')}
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <XCircleIcon className="h-12 w-12 mx-auto text-red-500 mb-4" />
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={() => fetchPaymentHistory(currentPage, pageSize)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                {t('paymentHistory.retry')}
              </button>
            </div>
          ) : paymentHistory.length > 0 ? (
            <>
              {/* Mobile-friendly Card Layout */}
              <div className="space-y-3">
                {paymentHistory.map((payment) => (
                  <div
                    key={payment.id}
                    className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CalendarIcon className="h-4 w-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-300">
                            {formatDateTime(payment.created)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          {getPaymentTypeBadge(payment.type)}
                          {getStatusBadge(payment.paymentStatus)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-slate-900 dark:text-slate-300">
                          {payment.amount.toFixed(2)} {payment.currency}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm text-slate-600 dark:text-slate-400" title={payment.name}>
                        {payment.name.length > 80 ? `${payment.name.substring(0, 80)}...` : payment.name}
                      </p>
                    </div>

                    {payment.refId && (
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <span>{t('paymentHistory.table.reference')}:</span>
                        <span className="font-mono">
                          {payment.refId.substring(0, 8)}...
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1 || loading}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === 1 || loading
                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                        }`}
                    >
                      ← {t('paymentHistory.pagination.previous')}
                    </button>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {t('paymentHistory.pagination.page')} {currentPage}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleNextPage}
                      disabled={!hasMorePages || loading}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${!hasMorePages || loading
                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                        }`}
                    >
                      {t('paymentHistory.pagination.next')} →
                    </button>
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {t('paymentHistory.pagination.total')}: {totalElements} {t('paymentHistory.pagination.payments')}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <CreditCardIcon className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-500 dark:text-slate-400 text-lg mb-2">
                {t('paymentHistory.noPayments')}
              </p>
              <p className="text-slate-400 dark:text-slate-500 text-sm">
                {t('paymentHistory.noPaymentsDescription')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;